import { useCallback, useEffect, useMemo, useState } from 'react';

import { useQuery } from '@tanstack/react-query';
import { format } from 'date-fns';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';

import { api, fetcher } from '@plugins/axios/api';

import { useAlert } from '@hooks/useAlert';

import { Button } from '@components/button/Button';
import { TableInputField } from '@components/field/TableInputField';
import { BasicTable } from '@components/table/BasicTable';
import { PageTitle } from '@components/title/PageTitle';

import { PageInfo, Paginated } from '@mocks/api';
import { ITEM_LIST, LEGACY_ITEM_LIST, ORDER_STATE } from '@mocks/projects';

import ItemSampleImg from '@assets/imgs/items/item_sample.png';

export const FOrderDetailPage = () => {
  const [orderDetail, setOrderDetail] = useState<{
    [name: string]: any;
  }>({});
  const { orderId } = useParams();
  const navigate = useNavigate();

  const { data, refetch, isFetching } = useQuery<any>({
    queryKey: [`/funeral/orders/${orderId}`],
    queryFn: fetcher,
    staleTime: 10 * 1000,
    enabled: orderId != undefined,
  });

  const { confirmClick } = useAlert();

  const tableInputCell = useCallback(
    (info: any) => {
      const itemId = info.getValue();
      return (
        <div className='flex flex-row'>
          <TableInputField
            className='w-[60px] border-none text-right'
            id={itemId}
            type='number'
            placeholder='0'
            disabled={orderId != undefined}
            initData={orderDetail[itemId] ?? 0}
            setData={setOrderDetail}
          />
          <p className='self-center pr-1 pt-[2px] text-gray-500'>박스</p>
        </div>
      );
    },
    [orderDetail],
  );

  const orderDetailList = useMemo(() => {
    let detaiList: number[] = [];
    Object.entries(orderDetail).forEach(([k, v]) => {
      if (v != undefined && v != 0 && v != '') {
        detaiList = detaiList.concat(Array(Number(v)).fill(k));
      }
    });
    return detaiList;
  }, [orderDetail]);

  const onDisableSubmit = useCallback(() => {
    confirmClick(
      { title: '주문을 취소하시겠습니까?' },
      {
        onClick: () => {
          api
            .delete(`/funeral/orders/${orderId}`)
            .then((res) => {
              refetch();
            })
            .catch((err) => {
              refetch();
              console.log(err);
            });
        },
      },
    );
  }, [orderId, refetch]);

  const itemList = useMemo(() => {
    let isLegacy = false;

    if (data && data['item'] && data['item']['insert_at']) {
      const insertAt = new Date(data['item']['insert_at']);
      const cutoffDate = new Date('2025-04-15');

      if (insertAt < cutoffDate) {
        isLegacy = true;
      }
    }

    const TMP_ITEM_LIST = isLegacy ? LEGACY_ITEM_LIST : ITEM_LIST;

    const keys = Object.keys(orderDetail);
    if (keys.length === 0) {
      return TMP_ITEM_LIST;
    } else {
      return TMP_ITEM_LIST.filter((item) =>
        keys.includes(String(item.item_id)),
      );
    }
  }, [orderDetail, data]);

  useEffect(() => {
    if (!isFetching && data != undefined) {
      console.log(data);

      if ('item' in data && 'order_items' in data['item']) {
        const orderItems: Array<number> = data['item']['order_items'] ?? [];
        const tmpOrderDetail: { [name: string]: number } = {};
        orderItems.forEach((itemId: number) => {
          if (itemId in tmpOrderDetail) {
            tmpOrderDetail[itemId] += 1;
          } else {
            tmpOrderDetail[itemId] = 1;
          }
        });
        setOrderDetail((prev) => ({ ...prev, ...tmpOrderDetail }));
      }
    }
  }, [isFetching, data]);

  useEffect(() => {
    if (orderId != undefined) {
      refetch();
    }
  }, [orderId]);

  return (
    <div className='container'>
      <PageTitle className='mt-4' titleHidden />
      <h1 className='text-2xl font-bold leading-[170%] tracking-tight'>
        다회용기 주문
        {data && data['item']['order_state'] == 404 && '(주문 취소)'}
      </h1>
      {data != undefined && (
        <section className='mt-3 flex flex-row flex-wrap rounded-lg border border-gray-200 p-4'>
          {[
            {
              title: '주문번호',
              value: data['item']['idx'],
            },
            {
              title: '총 수량',
              value: orderDetailList.length + ' 박스',
            },
          ].map((val, i) => {
            return (
              <div key={`menu-${i}`} className='mb-3 flex w-1/2 flex-row'>
                <p className='text-base font-bold'>{val['title']}</p>
                <p className='flex-1 text-center text-base'>{val['value']}</p>
              </div>
            );
          })}
          <div className='mb-3 flex w-full flex-row'>
            <p className='w-1/4 text-base font-bold'>주문상태</p>
            <p
              className={
                ORDER_STATE[data['item']['order_state']]['lable-class']
              }
            >
              {ORDER_STATE[data['item']['order_state']]['title']}
            </p>
          </div>
          {data && data['item']['partner_idx'] && (
            <>
              <div className='mb-3 flex w-full flex-row'>
                <p className='w-1/4 text-base font-bold'>배송업체</p>
                <p className='flex-1 text-start text-base'>
                  {data['item']['partner_name']}
                </p>
              </div>
              <div className='flex w-full flex-row'>
                <p className='w-1/4 text-base font-bold'>연락처</p>
                <p className='flex-1 text-start text-base'>
                  {data['item']['partner_phone']}
                </p>
              </div>
            </>
          )}
        </section>
      )}
      {data != undefined && (
        <section className='mt-3 flex flex-row flex-wrap space-y-5 rounded-lg p-4'>
          {[
            {
              title: '주문일자',
              value: format(
                new Date(data['item']['insert_at']),
                'yyyy-MM-dd HH시 mm분',
              ),
            },
            {
              title: '접수일자',
              value: format(
                new Date(data['item']['state_accept_at']),
                'yyyy-MM-dd HH시 mm분',
              ),
            },
            {
              title: '배송중',
              value: format(
                new Date(data['item']['state_complete_at']),
                'yyyy-MM-dd HH시 mm분',
              ),
            },
            {
              title: '배송완료',
              value: format(
                new Date(data['item']['state_complete_at']),
                'yyyy-MM-dd HH시 mm분',
              ),
            },
          ]
            .filter((_, i) => {
              let idxList = [];

              if (data['item']['order_state'] == 0) {
                idxList = [0];
              } else if (data['item']['order_state'] == 200) {
                idxList = [0, 1];
              } else if (data['item']['order_state'] == 202) {
                idxList = [0, 1, 2];
              } else if (data['item']['order_state'] == 300) {
                idxList = [0, 1, 3];
              } else {
                idxList = [0];
              }
              return idxList.includes(i);
            })
            .map((val, i) => {
              return (
                <div
                  key={`menu-${i}`}
                  className='flex w-full flex-row before:mr-3 before:inline-block before:h-2 before:w-2 before:self-center before:rounded-full before:bg-gray-500 last:before:bg-red-500'
                >
                  <p className='w-1/4 text-base font-bold'>{val['title']}</p>
                  <p className='flex-1 text-start text-base'>{val['value']}</p>
                </div>
              );
            })}
        </section>
      )}
      <img className='mt-3 h-full w-full object-cover' src={ItemSampleImg} />
      <section className='mt-6'>
        <BasicTable
          captionPosition='top'
          isLoading={isFetching}
          columns={[
            {
              header: '품번',
              accessorKey: 'item_id',
              minSize: 30,
              maxSize: 30,
            },
            {
              header: '품목',
              accessorKey: 'short_name',
              minSize: 140,
              maxSize: 140,
            },
            {
              header: '입수량\n(박스당)',
              accessorKey: 'unit_per',
              minSize: 100,
              maxSize: 100,
            },
            {
              header: '주문수량\n(박스)',
              accessorKey: 'count',
              minSize: 100,
              maxSize: 100,
              cell: tableInputCell,
            },
          ]}
          data={itemList}
          className='flex h-fit w-full flex-col gap-12'
        />
      </section>
      <section className='mt-6 flex flex-row justify-end pb-10'>
        {data && [0, 200].includes(data['item']['order_state']) && (
          <>
            <Button
              className='flex-1 rounded-r-none'
              onClick={() => navigate('/funeral/orders/' + orderId + '/edit')}
            >
              수정하기
            </Button>
            <Button
              className='flex-1 rounded-l-none bg-red-600'
              onClick={onDisableSubmit}
            >
              주문취소
            </Button>
          </>
        )}
      </section>
    </div>
  );
};
